import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../store/authStore";
import {
  CurrencyDollarIcon,
  CubeIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  ChartBarIcon,
  ShoppingCartIcon,
  QueueListIcon,
} from "@heroicons/react/24/outline";
import { InvestmentSummary } from "../components/organisms/InvestmentSummary";
import { RevenueSummary } from "../components/organisms/RevenueSummary";
import { PurchasesSummary } from "../components/organisms/PurchasesSummary";
import { InventorySummaryWidget } from "../components/organisms/InventorySummaryWidget";
import { SpendingSummary } from "../components/organisms/SpendingSummary";
import { OrderSummary } from "../components/organisms/OrderSummary";
import { KPIDashboardWidget } from "../components/organisms/KPIDashboardWidget";

interface DashboardCardProps {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
  accentColor: "primary" | "secondary" | "brand-teal";
}

function DashboardCard({ title, icon: Icon, children, accentColor }: DashboardCardProps) {
  const colorClasses = {
    primary: "border-l-primary-500",
    secondary: "border-l-secondary-500",
    "brand-teal": "border-l-brand-teal",
  };

  return (
    <div
      className={`bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 border-l-4 ${colorClasses[accentColor]} p-6`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-neutral-100 dark:bg-neutral-700 rounded-lg">
          <Icon className="w-6 h-6 text-neutral-600 dark:text-neutral-400" />
        </div>
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{title}</h3>
      </div>
      <div className="text-neutral-600 dark:text-neutral-400">{children}</div>
    </div>
  );
}

function PlaceholderContent({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-700 rounded-full flex items-center justify-center mb-4">
        <div className="w-8 h-8 bg-neutral-200 dark:bg-neutral-600 rounded animate-pulse" />
      </div>
      <p className="text-sm text-neutral-500 dark:text-neutral-400">{message}</p>
    </div>
  );
}

/** Roles that can see internal dashboard widgets (Story 6-6-12) */
const INTERNAL_DASHBOARD_ROLES = ["admin", "manager", "staff"] as const;

export function Dashboard() {
  const { t } = useTranslation("dashboard");
  const { user } = useAuthStore();
  const role = user?.role;
  const isCustomer = role === "customer";

  // Customer: default home is the portal product catalog (Epic 7); internal KPI dashboard N/A
  if (isCustomer) {
    return <Navigate to="/portal/products" replace />;
  }

  const canSeeFinancial = role === "admin" || role === "manager";
  const canSeeInternal =
    role !== undefined && (INTERNAL_DASHBOARD_ROLES as readonly string[]).includes(role);

  const welcomeName =
    user?.fullName || (role ? role.charAt(0).toUpperCase() + role.slice(1) : "User");

  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-100">
          {t("welcome", { name: welcomeName })}
        </h1>
        <p className="text-neutral-600 dark:text-neutral-400 mt-1">
          {t("business_overview")}
        </p>
      </div>

      {/* KPI Dashboard Section (Story 10-9: Admin only) */}
      {role === "admin" && (
        <DashboardCard
          title={t("kpi_title")}
          icon={ChartBarIcon}
          accentColor="brand-teal"
        >
          <KPIDashboardWidget />
        </DashboardCard>
      )}

      {/* Dashboard Cards Grid (Story 6-6-12: role-filtered; widgets hidden when no access) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Investment Summary: admin only */}
        {role === "admin" && (
          <DashboardCard
            title={t("investment_summary")}
            icon={CurrencyDollarIcon}
            accentColor="brand-teal"
          >
            <InvestmentSummary />
          </DashboardCard>
        )}

        {/* Revenue & Purchases: admin, manager */}
        {canSeeFinancial && (
          <>
            <DashboardCard title={t("revenue")} icon={ChartBarIcon} accentColor="brand-teal">
              <RevenueSummary />
            </DashboardCard>
            <DashboardCard title={t("purchases")} icon={ShoppingCartIcon} accentColor="secondary">
              <PurchasesSummary />
            </DashboardCard>
          </>
        )}

        {/* Inventory Status: admin, manager, staff */}
        {canSeeInternal && (
          <DashboardCard title={t("inventory_status")} icon={CubeIcon} accentColor="primary">
            <InventorySummaryWidget />
          </DashboardCard>
        )}

        {/* Spending Summary: admin, manager, staff */}
        {canSeeInternal && (
          <DashboardCard title={t("spending_summary")} icon={BanknotesIcon} accentColor="secondary">
            <SpendingSummary />
          </DashboardCard>
        )}

        {/* Order Summary: admin, manager, staff */}
        {canSeeInternal && (
          <DashboardCard
            title={t("order_summary")}
            icon={ClipboardDocumentListIcon}
            accentColor="brand-teal"
          >
            <OrderSummary />
          </DashboardCard>
        )}

        {/* Recent Orders: admin, manager, staff */}
        {canSeeInternal && (
          <DashboardCard title={t("recent_orders")} icon={QueueListIcon} accentColor="secondary">
            <PlaceholderContent message={t("no_recent_orders")} />
          </DashboardCard>
        )}
      </div>

    </div>
  );
}
